import { createEmailHistoryApiInstance, getForwardedHost } from 'utils/emailHistory/apiUtils';

/**
 * Email History API Client - Individual Emails
 * Base URL: /api3.2/emailHistory
 */

// Get the email history API instance
const emailHistoryApi = createEmailHistoryApiInstance('/api3.2/emailHistory');

/**
 * List all individual emails with pagination and filtering
 * @param {Object} params - Query parameters
 * @param {number} [params.page=0] - Page number (0-indexed)
 * @param {number} [params.pageSize=50] - Items per page
 * @param {string} [params.sortBy='sendDate'] - Field to sort by
 * @param {string} [params.sortOrder='desc'] - Sort direction ('asc' or 'desc')
 * @param {string} [params.type='all'] - Filter by email type ('all', 'campaign', 'individual')
 * @param {string} [params.status] - Filter by status
 * @param {string} [params.search] - Search by email address or subject
 * @param {string} [params.dateFrom] - Filter by send date (ISO 8601)
 * @param {string} [params.dateTo] - Filter by send date (ISO 8601)
 * @param {number} [params.contactId] - Filter by contact ID
 * @param {number} [params.bulkEmailId] - Filter by bulk email ID
 * @returns {Promise<{rows: Array, totalRows: number, page: number, pageSize: number}>}
 */
export async function fetchIndividualEmails(params = {}) {
  const {
    page = 0,
    pageSize = 50,
    sortBy = 'sendDate',
    sortOrder = 'desc',
    type = 'all',
    status = null,
    search = null,
    dateFrom = null,
    dateTo = null,
    contactId = null,
    bulkEmailId = null,
  } = params;

  const queryParams = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    sortBy,
    sortOrder,
    type,
  });

  if (status) {
    queryParams.set('status', status);
  }

  if (search && search.trim()) {
    queryParams.set('search', search.trim());
  }

  if (dateFrom) {
    queryParams.set('dateFrom', dateFrom);
  }

  if (dateTo) {
    queryParams.set('dateTo', dateTo);
  }

  if (contactId) {
    queryParams.set('contactId', String(contactId));
  }

  if (bulkEmailId) {
    queryParams.set('bulkEmailId', String(bulkEmailId));
  }

  try {
    const endpoint = `/emails?${queryParams.toString()}`;
    const { data } = await emailHistoryApi.get(endpoint, {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'x-forwarded-host': getForwardedHost(emailHistoryApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch individual emails');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching individual emails:', error);
    throw error;
  }
}

/**
 * Get comprehensive details for a specific individual email
 * @param {number} individualEmailId - The individual email ID
 * @returns {Promise<Object>} Individual email details with events
 */
export async function fetchIndividualEmailDetails(individualEmailId) {
  try {
    const { data } = await emailHistoryApi.get(`/emails/${individualEmailId}`, {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'x-forwarded-host': getForwardedHost(emailHistoryApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch individual email details');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching individual email details:', error);
    throw error;
  }
}

